<script setup>
import { usePlatformApi } from '@/utils/platformApi'
import { emailValidator, requiredValidator } from '@core/utils/validators'

definePage({ meta: { layout: 'platform', public: true } })

const route = useRoute()
const router = useRouter()

const platformAccessToken = useCookie('platformAccessToken')
const platformAdminData = useCookie('platformAdminData')

if (!platformAccessToken.value)
  await router.push('/platform/login')

const form = ref({
  name: '',
  email: '',
  phone: '',
  address: '',
  is_active: true,
})

// Slug ditampilkan read-only sebagai konteks, tidak ikut dikirim saat submit.
const slug = ref('')
const usersCount = ref(0)

const formRef = ref()
const fetchLoading = ref(true)
const fetchError = ref('')
const loading = ref(false)
const errors = ref({})
const successMessage = ref('')

// ─── Reset Password Owner ──────────────────────────────────────────────────
// Password SELALU digenerate server (bukan admin yang mengetik password
// untuk orang lain) -- lihat PlatformTenantService::resetOwnerPassword().
// Hasilnya juga ditampilkan langsung di sini (bukan cuma dikirim email),
// supaya admin tetap bisa memberitahu owner manual kalau pengiriman email
// gagal/lambat.
const resetPasswordLoading = ref(false)
const resetPasswordError = ref('')
const resetPasswordResult = ref(null)
const confirmResetDialog = ref(false)

const resetOwnerPassword = async () => {
  confirmResetDialog.value = false
  resetPasswordLoading.value = true
  resetPasswordError.value = ''
  resetPasswordResult.value = null

  try {
    const res = await usePlatformApi(`/api/v1/platform/tenants/${route.params.id}/reset-owner-password`).post()
    const body = res?.data ?? res

    resetPasswordResult.value = body?.data ?? body
  } catch (err) {
    if (await handleAuthError(err))
      return

    const data = err?.data ?? err?.response?._data

    resetPasswordError.value = data?.message ?? 'Gagal mereset password owner.'
  } finally {
    resetPasswordLoading.value = false
  }
}

const handleAuthError = async err => {
  const status = err?.status ?? err?.response?.status
  if (status === 401 || status === 403) {
    platformAccessToken.value = null
    platformAdminData.value = null
    await router.push('/platform/login')

    return true
  }

  return false
}

onMounted(async () => {
  try {
    const res = await usePlatformApi(`/api/v1/platform/tenants/${route.params.id}`).get()
    const body = res?.data ?? res
    const t = body?.data ?? body

    form.value = {
      name:      t.name      ?? '',
      email:     t.email     ?? '',
      phone:     t.phone     ?? '',
      address:   t.address   ?? '',
      is_active: t.is_active ?? true,
    }
    slug.value = t.slug ?? ''
    usersCount.value = t.users_count ?? 0
  } catch (err) {
    if (await handleAuthError(err))
      return

    fetchError.value = 'Gagal memuat data tenant.'
    console.error('[Platform] fetch tenant error', err)
  } finally {
    fetchLoading.value = false
  }
})

const submit = async () => {
  const { valid } = await formRef.value?.validate()
  if (!valid)
    return

  loading.value = true
  errors.value = {}
  successMessage.value = ''

  try {
    const res = await usePlatformApi(`/api/v1/platform/tenants/${route.params.id}`).put({ body: form.value })
    const body = res?.data ?? res

    successMessage.value = body?.message ?? 'Tenant berhasil diperbarui.'

    setTimeout(() => router.push('/platform/tenants'), 600)
  } catch (err) {
    if (await handleAuthError(err))
      return

    const status = err?.status ?? err?.response?.status
    const data = err?.data ?? err?.response?._data

    if (status === 422 && data?.errors)
      errors.value = data.errors
    else
      errors.value = { _general: [data?.message ?? 'Gagal memperbarui tenant.'] }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="pa-6 d-flex justify-center">
    <VCard
      max-width="640"
      class="w-100"
    >
      <VCardTitle class="pt-5 px-5">
        Edit Tenant
      </VCardTitle>
      <VCardSubtitle class="px-5 pb-2">
        Slug: <code>{{ slug }}</code> (tidak bisa diubah) &middot; {{ usersCount }} user terdaftar
      </VCardSubtitle>

      <VDivider />

      <!-- Loading state -->
      <VCardText v-if="fetchLoading" class="pa-10 text-center">
        <VProgressCircular indeterminate color="primary" />
      </VCardText>

      <!-- Fetch error -->
      <VCardText v-else-if="fetchError" class="pa-5">
        <VAlert type="error" variant="tonal">
          {{ fetchError }}
        </VAlert>
      </VCardText>

      <VCardText v-else class="pa-5">
        <VAlert
          v-if="successMessage"
          type="success"
          variant="tonal"
          class="mb-4"
        >
          {{ successMessage }}
        </VAlert>

        <VAlert
          v-if="errors._general"
          type="error"
          variant="tonal"
          class="mb-4"
        >
          {{ errors._general[0] }}
        </VAlert>

        <VForm
          ref="formRef"
          @submit.prevent="submit"
        >
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="form.name"
                label="Nama Tenant *"
                :rules="[requiredValidator]"
                :error-messages="errors.name"
              />
            </VCol>

            <VCol cols="12" md="6">
              <VTextField
                v-model="form.email"
                label="Email Tenant *"
                type="email"
                :rules="[requiredValidator, emailValidator]"
                :error-messages="errors.email"
              />
            </VCol>

            <VCol cols="12" md="6">
              <VTextField
                v-model="form.phone"
                label="Telepon"
                :error-messages="errors.phone"
              />
            </VCol>

            <VCol cols="12" class="d-flex align-center">
              <VSwitch
                v-model="form.is_active"
                label="Tenant Aktif"
                color="success"
                hide-details
              />
            </VCol>

            <VCol cols="12">
              <VTextarea
                v-model="form.address"
                label="Alamat"
                rows="2"
                :error-messages="errors.address"
              />
            </VCol>
          </VRow>
        </VForm>
      </VCardText>

      <VDivider />

      <VCardActions class="pa-5">
        <VSpacer />
        <VBtn
          variant="tonal"
          :to="{ path: '/platform/tenants' }"
        >
          Batal
        </VBtn>
        <VBtn
          color="primary"
          :loading="loading"
          :disabled="fetchLoading || !!fetchError"
          @click="submit"
        >
          Simpan Perubahan
        </VBtn>
      </VCardActions>
    </VCard>
  </div>

  <!-- Reset Password Owner -->
  <div v-if="!fetchLoading && !fetchError" class="pa-6 pt-0 d-flex justify-center">
    <VCard max-width="640" class="w-100">
      <VCardTitle class="pt-5 px-5">
        Reset Password Owner
      </VCardTitle>
      <VCardSubtitle class="px-5 pb-2">
        Buat password baru untuk akun Owner tenant ini -- dipakai kalau email pendaftaran
        tidak pernah sampai, atau owner lupa password.
      </VCardSubtitle>

      <VDivider />

      <VCardText class="pa-5">
        <VAlert
          v-if="resetPasswordError"
          type="error"
          variant="tonal"
          class="mb-4"
          closable
          @click:close="resetPasswordError = ''"
        >
          {{ resetPasswordError }}
        </VAlert>

        <VAlert
          v-if="resetPasswordResult"
          :type="resetPasswordResult.email_sent === false ? 'warning' : 'success'"
          variant="tonal"
        >
          <p class="mb-2">
            Password owner ({{ resetPasswordResult.owner_email }}) berhasil direset.
            <template v-if="resetPasswordResult.email_sent === false">
              <strong>Email notifikasi GAGAL terkirim</strong> — sampaikan password baru ini ke
              owner secara manual.
            </template>
            <template v-else>
              Email notifikasi sudah dikirim. Password baru:
            </template>
          </p>
          <VTextField
            :model-value="resetPasswordResult.plain_password"
            readonly
            density="compact"
            variant="outlined"
            hide-details
            append-inner-icon="ri-file-copy-line"
            @click:append-inner="navigator.clipboard.writeText(resetPasswordResult.plain_password)"
          />
        </VAlert>

        <p v-else class="text-body-2 text-medium-emphasis mb-0">
          Password baru akan digenerate otomatis (bukan diketik manual) dan dikirim ke email owner.
        </p>
      </VCardText>

      <VDivider />

      <VCardActions class="pa-5">
        <VSpacer />
        <VBtn
          color="warning"
          variant="tonal"
          :loading="resetPasswordLoading"
          @click="confirmResetDialog = true"
        >
          Reset Password Owner
        </VBtn>
      </VCardActions>
    </VCard>
  </div>

  <VDialog v-model="confirmResetDialog" max-width="440">
    <VCard>
      <VCardTitle class="pt-5 px-5">
        Reset password owner?
      </VCardTitle>
      <VCardText>
        Password owner tenant <strong>{{ form.name }}</strong> akan diganti dengan password baru
        secara acak. Password lama tidak akan bisa dipakai lagi setelah ini.
      </VCardText>
      <VCardActions class="pa-5 pt-0">
        <VSpacer />
        <VBtn variant="tonal" @click="confirmResetDialog = false">
          Batal
        </VBtn>
        <VBtn color="warning" @click="resetOwnerPassword">
          Ya, Reset Password
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
